import React, { useState } from 'react';
import { toast } from 'react-toastify';
import ToggleSwitch from 'components/ToggleSwitch';
import InlineFoilPills from 'components/InlineFoilPills/InlineFoilPills';
import InlineConditionPills from 'components/InlineConditionPills/InlineConditionPills';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';
import SmsAlertsIntroModal from 'components/SmsAlertsIntroModal/SmsAlertsIntroModal';
import { EliteUpsellPrompt } from 'components/EliteUpsellPrompt/EliteUpsellPrompt';
import { AssetUrls } from 'config/assets';
import { WANT_LIST_STOCK_DEFAULTS } from 'constants/entryDefaults';
import { SUPPORTED_LANGUAGES } from 'constants/languages';
import { getMonthlyAlertLimit } from 'constants/haveListAlerts';
import { useCanUseHaveListAlerts } from 'hooks/useCanUseHaveListAlerts';
import { useSmsAlertsIntroGate } from 'hooks/useSmsAlertsIntroGate';
import { useGetMeQuery, useUpdateTradingPreferencesMutation } from 'store/services/user/meApi';
import {
  useGetWantListDefaultsQuery,
  useUpdateWantListDefaultsMutation,
  useGetHaveListDefaultsQuery,
  useUpdateHaveListDefaultsMutation,
} from 'store/services/settings/settingsApi';
import {
  useGetUiPreferencesQuery,
  useUpdateUiPreferencesMutation,
} from 'store/services/settings/uiPreferencesApi';
import { isPhoneNotVerifiedError, isFeatureNotAvailableError } from 'utils/haveListAlertErrors';
import { CardFinish, wantAcceptedFinishes } from 'utils/finishes';
import { SettingsCard, SettingsRow, SettingsSection, VacationChip } from '../SettingsRow/SettingsRow';
import VacationModeCard from '../VacationModeCard/VacationModeCard';
import { useVacationMode } from 'hooks/useVacationMode';
import '../../Settings.css';

const LANGUAGES = SUPPORTED_LANGUAGES.map((l) => ({ code: l.code, name: l.displayName }));
const SAVE_FAILED = 'Failed to update preference. Please try again.';

/**
 * Settings > Preferences: Want List defaults, Have List defaults, Matching, Trading.
 * Every control saves on change. The mutations patch their cache at once and
 * undo on failure, so the only feedback needed is a toast when a save fails.
 */
const PreferencesTab: React.FC = () => {
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const vacation = useVacationMode();

  const { data: userData } = useGetMeQuery();
  const [updateTradingPreferences, { isLoading: isUpdatingTradingPrefs }] = useUpdateTradingPreferencesMutation();
  const [showDomesticTradesUpsell, setShowDomesticTradesUpsell] = useState(false);
  const [showAlertsQuotaUpsell, setShowAlertsQuotaUpsell] = useState(false);
  const canUseAlerts = useCanUseHaveListAlerts();
  // Region-aware Elite cap, shared by the quota upsell link and the modal it opens
  // so the two can't disagree (an international free user must see their own
  // number in both places, not the US one).
  const eliteAlertCap = getMonthlyAlertLimit(true, userData?.country ?? null);
  const smsIntroGate = useSmsAlertsIntroGate();
  const { data: wantListDefaults, isLoading: isLoadingWantListDefaults } = useGetWantListDefaultsQuery();
  const [updateWantListDefaults] = useUpdateWantListDefaultsMutation();
  const { data: haveListDefaults, isLoading: isLoadingHaveListDefaults } = useGetHaveListDefaultsQuery();
  const [updateHaveListDefaults, { isLoading: isUpdatingHaveListDefaults }] = useUpdateHaveListDefaultsMutation();

  const saveWantDefaults = async (patch: Parameters<typeof updateWantListDefaults>[0]) => {
    try {
      await updateWantListDefaults(patch).unwrap();
    } catch (error: any) {
      console.error('Failed to update want list defaults:', error);
      toast.error(SAVE_FAILED);
    }
  };

  // One set argument (Design Pass engineering note), not a third positional boolean
  const handleFoilChange = (accepted: CardFinish[]) => {
    if (accepted.length === 0) {
      toast.error('At least one finish must be selected');
      return;
    }
    saveWantDefaults({
      defaultAcceptNonFoil: accepted.includes('nonfoil'),
      defaultAcceptFoil: accepted.includes('foil'),
      defaultAcceptEtched: accepted.includes('etched'),
    });
  };

  const handleConditionChange = (conditions: string[]) => {
    if (conditions.length === 0) {
      toast.error('At least one condition must be selected');
      return;
    }
    saveWantDefaults({ defaultConditions: conditions });
  };

  const handleLanguageChange = (languages: string[]) => {
    if (languages.length === 0) {
      toast.error('At least one language must be selected');
      return;
    }
    saveWantDefaults({ defaultLanguages: languages });
  };

  // Trading: the Complete Trade confirmation (Asana 1219704178739168). The toggle
  // reads the server field inverted: ON = show the modal (field false or absent),
  // OFF = suppressed (field true). The mutation patches the cache at once and
  // undoes on failure, so a failed save reverts the toggle by itself.
  const { data: uiPrefs, isLoading: isLoadingUiPrefs } = useGetUiPreferencesQuery();
  const [updateUiPreferences, { isLoading: isUpdatingUiPrefs }] = useUpdateUiPreferencesMutation();
  const showCompleteTradeConfirmation = uiPrefs?.hideCompleteTradeConfirmation !== true;
  const handleCompleteTradeConfirmationToggle = async (checked: boolean) => {
    try {
      await updateUiPreferences({ hideCompleteTradeConfirmation: !checked }).unwrap();
    } catch (error: any) {
      console.error('Failed to update trade confirmation preference:', error);
      toast.error(SAVE_FAILED);
    }
  };

  const saveHaveDefaults = async (patch: Parameters<typeof updateHaveListDefaults>[0]) => {
    try {
      await updateHaveListDefaults(patch).unwrap();
    } catch (error: any) {
      console.error('Failed to update have list defaults:', error);
      toast.error(SAVE_FAILED);
    }
  };

  const handleDefaultHaveListAlertToggle = async (checked: boolean) => {
    const applyToggle = async () => {
      if (checked && !userData?.phoneVerified) {
        setIsPhoneModalOpen(true);
        return;
      }
      try {
        await updateHaveListDefaults({ defaultHaveListAlert: checked }).unwrap();
      } catch (error) {
        if (isPhoneNotVerifiedError(error)) {
          setIsPhoneModalOpen(true);
        } else if (isFeatureNotAvailableError(error)) {
          console.warn('[Settings] FEATURE_NOT_AVAILABLE returned for alerts — gating may be misaligned');
        } else {
          console.error('Failed to update default alert preference:', error);
          toast.error('Failed to update default alert preference');
        }
      }
    };
    // Enabling auto-alerts is gated behind the first-time SMS Alerts intro modal;
    // the toggle (controlled by server state) only moves once the mutation runs.
    if (checked) {
      smsIntroGate.guardEnable(applyToggle);
    } else {
      await applyToggle();
    }
  };

  const handlePreferDomesticTradesToggle = async (checked: boolean) => {
    if (!userData?.isElite) {
      setShowDomesticTradesUpsell(true);
      return;
    }
    try {
      await updateTradingPreferences({ preferDomesticTrades: checked }).unwrap();
    } catch (error: any) {
      console.error('Failed to update trading preferences:', error);
      if (error?.status === 403) {
        setShowDomesticTradesUpsell(true);
        return;
      }
      toast.error(SAVE_FAILED);
    }
  };

  const selectedLanguages = wantListDefaults?.defaultLanguages ?? WANT_LIST_STOCK_DEFAULTS.languages;
  const addReceived = haveListDefaults?.addReceivedCardsToHaveList ?? true;
  const alertsSent = haveListDefaults?.alertsSentThisMonth ?? 0;
  const alertsLimit =
    haveListDefaults?.alertsMonthlyLimit ?? getMonthlyAlertLimit(!!userData?.isElite, userData?.country ?? null);

  return (
    <>
      <SettingsSection label="Want List defaults">
        <SettingsCard>
          <SettingsRow
            stacked
            title="Finish"
            description="The finishes a new want accepts."
            control={
              <InlineFoilPills
                value={wantAcceptedFinishes({
                  acceptFoil: wantListDefaults?.defaultAcceptFoil ?? true,
                  acceptNonFoil: wantListDefaults?.defaultAcceptNonFoil ?? true,
                  acceptEtched: wantListDefaults?.defaultAcceptEtched,
                })}
                onChange={handleFoilChange}
                disabled={isLoadingWantListDefaults}
              />
            }
          />
          <SettingsRow
            stacked
            title="Condition"
            description="The conditions a new want accepts."
            control={
              <InlineConditionPills
                value={wantListDefaults?.defaultConditions ?? WANT_LIST_STOCK_DEFAULTS.conditions}
                onChange={handleConditionChange}
                disabled={isLoadingWantListDefaults}
              />
            }
          />
          <SettingsRow
            stacked
            title="Language"
            description="The languages a new want accepts."
            control={
              <div className="language-pills-container">
                {LANGUAGES.map((lang) => {
                  const isActive = selectedLanguages.includes(lang.code);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      className={`filter-pill ${isActive ? 'active' : ''}`}
                      aria-pressed={isActive}
                      onClick={() =>
                        handleLanguageChange(
                          isActive
                            ? selectedLanguages.filter((l) => l !== lang.code)
                            : [...selectedLanguages, lang.code],
                        )
                      }
                      disabled={isLoadingWantListDefaults}
                    >
                      <span>{lang.name}</span>
                      {isActive && (
                        <img src={AssetUrls.icons.dropdowns.close} alt="" className="filter-pill-close" />
                      )}
                    </button>
                  );
                })}
              </div>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <VacationModeCard />

      <SettingsSection label="Have List defaults">
        <SettingsCard>
          <SettingsRow
            title="Add received cards to Have List"
            description="When you receive cards in trades, add them to your Have List."
            control={
              <ToggleSwitch
                checked={addReceived}
                onChange={(checked) => saveHaveDefaults({ addReceivedCardsToHaveList: checked })}
                disabled={isLoadingHaveListDefaults || isUpdatingHaveListDefaults}
                ariaLabel="Add received cards to Have List"
              />
            }
          />
          {/* A card that never reaches the Have List can't default to For Trade. */}
          <SettingsRow
            dependent
            disabled={!addReceived}
            title="Mark them For Trade"
            description={
              addReceived
                ? 'Cards you receive are added as available for trading.'
                : 'Needs the setting above turned on'
            }
            control={
              <ToggleSwitch
                checked={haveListDefaults?.receivedCardsForTrade ?? false}
                onChange={(checked) => saveHaveDefaults({ receivedCardsForTrade: checked })}
                disabled={isLoadingHaveListDefaults || isUpdatingHaveListDefaults || !addReceived}
                ariaLabel="Mark received cards For Trade"
              />
            }
          />
          {canUseAlerts && (
            <SettingsRow
              title={
                <>
                  Auto-enable alerts on new cards
                  {/* Vacation pauses the alerts themselves; the stored setting is not touched. */}
                  {vacation.active && <VacationChip>Paused while away</VacationChip>}
                </>
              }
              description={
                <>
                  Cards you add to your Have List will have alerts on by default. Toggle individual cards anytime.
                  {!userData?.phoneVerified && (
                    <>
                      {' '}
                      <button type="button" className="settings-inline-link" onClick={() => setIsPhoneModalOpen(true)}>
                        Verify phone number
                      </button>
                    </>
                  )}
                </>
              }
              below={
                <div className="settings-row-item__desc">
                  {alertsSent} of {alertsLimit} alerts sent this month.
                  {!userData?.isElite && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="settings-inline-link"
                        onClick={() => setShowAlertsQuotaUpsell(true)}
                      >
                        {`Upgrade to Elite for ${eliteAlertCap}/month`}
                      </button>
                    </>
                  )}
                </div>
              }
              control={
                <ToggleSwitch
                  checked={haveListDefaults?.defaultHaveListAlert ?? false}
                  onChange={handleDefaultHaveListAlertToggle}
                  disabled={!userData?.phoneVerified || isLoadingHaveListDefaults || isUpdatingHaveListDefaults}
                  ariaLabel="Auto-enable alerts on new cards"
                />
              }
            />
          )}
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Matching">
        <SettingsCard>
          <SettingsRow
            title={
              <>
                Prefer domestic trades
                <img src={AssetUrls.icons.elite.badge} alt="Elite feature" className="settings-elite-lock-badge" />
              </>
            }
            description="Surface same-country traders first on the Send Cards page."
            control={
              <ToggleSwitch
                checked={userData?.isElite ? (userData?.preferDomesticTrades ?? false) : false}
                onChange={handlePreferDomesticTradesToggle}
                disabled={userData?.isElite && isUpdatingTradingPrefs}
                ariaLabel="Prefer domestic trades"
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Trading">
        <SettingsCard>
          <SettingsRow
            title="Show confirmation when completing trades"
            description={
              showCompleteTradeConfirmation
                ? 'Check edition, condition and language before points release to your sender.'
                : 'Off. Complete Trade releases points straight away, with no check.'
            }
            control={
              <ToggleSwitch
                checked={showCompleteTradeConfirmation}
                onChange={handleCompleteTradeConfirmationToggle}
                disabled={isLoadingUiPrefs || isUpdatingUiPrefs}
                ariaLabel="Show confirmation when completing trades"
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      {canUseAlerts && (
        <EliteUpsellPrompt
          isOpen={showAlertsQuotaUpsell}
          onClose={() => setShowAlertsQuotaUpsell(false)}
          source="have-list-alert-quota"
          title="More Alerts is an Elite Feature"
          body={`Elite traders get ${eliteAlertCap} Have List alerts per month — never miss a high-demand card again.`}
          learnMoreLabel="Upgrade to Elite"
        />
      )}
      <EliteUpsellPrompt
        isOpen={showDomesticTradesUpsell}
        onClose={() => setShowDomesticTradesUpsell(false)}
        source="prefer-domestic-trades"
        title="Prefer Domestic Trades is an Elite Feature"
        body="Prioritize traders in your country without lifting a finger — set it once, benefit every session."
        emojiRow={
          <>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#127758;</span> 3
            </span>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#9992;&#65039;</span> 2
            </span>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#129309;</span> 1
            </span>
          </>
        }
        learnMoreLabel="Upgrade to Elite"
      />

      <SmsAlertsIntroModal
        isOpen={smsIntroGate.isModalOpen}
        onConfirm={smsIntroGate.handleConfirm}
        onDismiss={smsIntroGate.handleDismiss}
      />

      <MobileVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        currentPhone={userData?.phoneNumber}
        onVerificationSuccess={() => {
          // Refresh user data to get updated phone verification status
          window.location.reload();
        }}
      />
    </>
  );
};

export default PreferencesTab;
